import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import MultiraceLogo from '@/components/elements/Logo/MultiraceLogo';
import RaceProgramDetail from '@/components/layouts/Race/Singlerace/RaceProgramInfo/RaceProgramDetail';
import ResultUI, { ResultsTemplateProps } from '@/components/templates/Results';
import {
  MultiraceProgram,
  MultiraceType,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { getVelodromeByCode } from '@/lib/race/velodrome';
import { CurrentMultiraceProgramMap, LotteryType } from '@/modelTypes';
import { toHalfWidth } from '@/utility/utils';

import { NoRaceText, RaceProgramListWrapper } from '../style';
import {
  DisabledLink,
  List,
  ListWrapper,
  LogoArea,
  Logos,
  ProgramInfo,
  ResultArea,
  StyledLink,
} from './style';

interface MultiraceResultsTemplateProps extends ResultsTemplateProps {
  multiracePrograms: MultiraceProgram[];
  loading: boolean;
}

const MultiraceResultsTemplate: React.FC<MultiraceResultsTemplateProps> = ({
  date,
  raceType,
  setRaceType,
  multiracePrograms,
  loading,
}) => {
  const multiraceList = () => {
    const list = {} as CurrentMultiraceProgramMap;
    multiracePrograms.forEach(mrp => {
      if (!list[mrp.velodromeCode]) {
        list[mrp.velodromeCode] = [];
      }
      list[mrp.velodromeCode].push(mrp);
    });
    return list;
  };

  const getMultiraceTypes = (mrps: MultiraceProgram[], type: LotteryType) =>
    Array.from(
      new Set(mrps.map(mrp => mrp.multiraceType).filter(t => t.match(type))),
    );

  const isDokanto = (type: MultiraceType) =>
    [MultiraceType.Dokanto4two, MultiraceType.Dokanto7].includes(type);

  return (
    <ResultUI date={date} raceType={raceType} setRaceType={setRaceType}>
      <RaceProgramListWrapper isShow={multiracePrograms.length > 0}>
        <ListWrapper component="ul">
          {Object.entries(multiraceList()).map(([code, mrps], i) => (
            <CSSTransition key={`${code}-${i}`} timeout={200}>
              {Boolean(mrps.length) && (
                <List>
                  <ProgramInfo>
                    <RaceProgramDetail
                      raceProgram={mrps[0].raceProgram}
                      hasLink={mrps[0].releaseDate.isBefore(dayjst())}
                      href={`/${
                        getVelodromeByCode(mrps[0].raceProgram.velodromeCode)
                          .romanName
                      }/race/${mrps[0].raceProgram.heldDate.format(
                        'YYYY-MM-DD',
                      )}/result`}
                    />
                  </ProgramInfo>
                  <p>{toHalfWidth(mrps[0].programName)}</p>
                  <ResultArea>
                    <LogoArea>
                      {Object.values(LotteryType).map(lotteryType => (
                        <React.Fragment key={`${lotteryType}-${i}`}>
                          {Boolean(
                            getMultiraceTypes(mrps, lotteryType).length,
                          ) && (
                            <Logos>
                              {getMultiraceTypes(mrps, lotteryType).map(
                                (type, j) => (
                                  <MultiraceLogo
                                    key={`${type}-${i}-${j}`}
                                    multiraceType={type}
                                    width={!isDokanto(type) ? '52px' : ''}
                                    height={isDokanto(type) ? '20px' : ''}
                                  />
                                ),
                              )}
                            </Logos>
                          )}
                        </React.Fragment>
                      ))}
                    </LogoArea>
                    {mrps[0].releaseDate.isAfter(dayjst()) ? (
                      <DisabledLink>結果を見る</DisabledLink>
                    ) : (
                      <StyledLink
                        to={`/${
                          getVelodromeByCode(mrps[0].velodromeCode).romanName
                        }/lottery/${mrps[0].heldDate.format(
                          'YYYY-MM-DD',
                        )}/result`}
                      >
                        結果を見る
                      </StyledLink>
                    )}
                  </ResultArea>
                </List>
              )}
            </CSSTransition>
          ))}
        </ListWrapper>
      </RaceProgramListWrapper>
      <CSSTransition
        in={multiracePrograms.length === 0 && !loading}
        timeout={200}
        unmountOnExit
      >
        <NoRaceText>開催情報がありません</NoRaceText>
      </CSSTransition>
    </ResultUI>
  );
};

export default MultiraceResultsTemplate;
